import { useAsyncModal } from '@ipfabric/frontend-shared-assets/utils/react/useAsyncModal';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { ButtonAccent } from 'components/Button/Button';
import {
  ConfirmDialog,
  type ConfirmDialogMessages,
  type ConfirmDialogOwnProps,
} from 'containers/ConfirmDialog/ConfirmDialog';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

export type SimpleConfirmDialogHook = {
  show: (
    messages: ConfirmDialogMessages,
    confirmDialogProps?: Partial<ConfirmDialogOwnProps>,
  ) => Promise<boolean>;
  render: () => ReactNode;
  close: () => void;
};

export interface SimpleConfirmDialogProps {
  btnConfirmAccent?: ButtonAccent;
  btnCancelAccent?: ButtonAccent;
}

type DialogContent = {
  messages: ConfirmDialogMessages;
  props?: Partial<ConfirmDialogOwnProps>;
};

/**
 *
 * @example
 * const confirmDeleteDialog = useSimpleConfirmDialog();
 *
 * const handleSomething = async () => {
 *   if (await confirmDeleteDialog.show(
 *     {
 *       btnConfirm: 'Yes',
 *       btnSecondaryConfirm: 'No', // optiona, actually "reject", misleading name due to legacy reasons
 *       header: 'Do it?',
 *       subHeader: 'Are you sure?', // optional
 *     },
 *     {children: <p>Something more to say in dialog body</p>},
 *   )) {
 *     console.log('Yep, do it.');
 *   }
 * }
 *
 * // somewhere in component JSX body
 * {confirmDeleteDialog.render()}
 */
export const useSimpleConfirmDialog = (
  props?: SimpleConfirmDialogProps,
): SimpleConfirmDialogHook => {
  const modal = useAsyncModal<boolean>();
  const [content, setContent] = useState<DialogContent | null>(null);
  const returnFromModalRef = useRef<((value: boolean) => void) | null>(null);

  const show = useStableCallback(async (messages, dialogProps) => {
    setContent({ messages, props: dialogProps });
    try {
      return await modal.show();
    } finally {
      setContent(null);
      returnFromModalRef.current = null;
    }
  });

  const close = useStableCallback(() => {
    if (returnFromModalRef.current) {
      returnFromModalRef.current(false);
    }
  });

  return {
    render: () =>
      modal.render((returnFromModal) => {
        returnFromModalRef.current = returnFromModal;
        return (
          content && (
            <ConfirmDialog
              btnCancelAccent={props?.btnCancelAccent || 'positive'}
              btnCancelDataTestId={content.props?.btnCancelDataTestId}
              btnCancelVariant='secondary'
              btnConfirmAccent={props?.btnConfirmAccent || 'negative'}
              btnConfirmDataTestId={content.props?.btnConfirmDataTestId}
              btnConfirmVariant='primary'
              msg={content.messages}
              onClickConfirm={() => returnFromModal(true)}
              onClickSecondaryConfirm={() => returnFromModal(false)}
              onCloseClick={() => returnFromModal(false)}
              {...content.props}
            />
          )
        );
      }),
    show,
    close,
  };
};
